import React, { useEffect, useState } from "react";
import { Sparkles, Quote } from "lucide-react";
import { useI18n } from "../context/I18nContext";
import { api } from "../lib/api";

export default function About() {
  const { t } = useI18n();
  const [site, setSite] = useState({});
  const [testimonials, setTestimonials] = useState([]);
  const [team, setTeam] = useState([]);
  const values = [
    { t: t("about.value1t"), d: t("about.value1d") },
    { t: t("about.value2t"), d: t("about.value2d") },
    { t: t("about.value3t"), d: t("about.value3d") },
    { t: t("about.value4t"), d: t("about.value4d") },
    { t: t("about.value5t"), d: t("about.value5d") },
  ];
  useEffect(() => {
    api.get("/settings").then(r => setSite(r.data?.site || {})).catch(() => {});
    api.get("/testimonials").then(r => setTestimonials(r.data || [])).catch(() => {});
    api.get("/team").then(r => setTeam(r.data || [])).catch(() => {});
  }, []);
  return (
    <div className="bg-ivory" data-testid="about-page">
      <section className="hero-gradient py-24">
        <div className="max-w-6xl mx-auto px-5 lg:px-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-warm-orange/10 text-warm-orange border border-warm-orange/30 mb-6">
            <Sparkles size={14}/> <span className="text-xs uppercase tracking-[0.25em] font-semibold">{t("pages.about.tag")}</span>
          </div>
          <h1 className="font-display text-deep-blue text-5xl sm:text-6xl lg:text-7xl leading-[1.05]">{t("pages.about.t1")}<br/><span className="italic text-warm-orange">{t("pages.about.t2")}</span></h1>
          <p className="text-deep-blue/70 mt-8 max-w-3xl text-lg leading-relaxed">{t("pages.about.intro")}</p>
        </div>
      </section>
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-5 lg:px-10 grid md:grid-cols-3 gap-8">
          <div className="p-8 rounded-2xl bg-pearl">
            <div className="text-xs uppercase tracking-[0.2em] text-gold">{t("about.visionTag")}</div>
            <p className="font-display text-3xl text-deep-blue mt-3 leading-tight">{t("about.visionText")}</p>
          </div>
          <div className="p-8 rounded-2xl bg-deep-blue text-ivory">
            <div className="text-xs uppercase tracking-[0.2em] text-gold">{t("about.missionTag")}</div>
            <p className="font-display text-3xl mt-3 leading-tight">{t("about.missionText")}</p>
          </div>
          <div className="p-8 rounded-2xl bg-gold text-deep-blue">
            <div className="text-xs uppercase tracking-[0.2em]">{t("about.promiseTag")}</div>
            <p className="font-display text-3xl mt-3 leading-tight">{t("about.promiseText")}</p>
          </div>
        </div>
      </section>
      <section className="py-20 bg-pearl">
        <div className="max-w-6xl mx-auto px-5 lg:px-10">
          <h2 className="font-display text-deep-blue text-4xl sm:text-5xl mb-10">{t("about.valuesTitle")}</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4">
            {values.map((v, i) => (
              <div key={v.t} className="card-elev rounded-xl p-6" data-testid={`value-${i}`}>
                <div className="text-warm-orange font-display text-2xl">0{i+1}</div>
                <h3 className="font-display text-xl text-deep-blue mt-2">{v.t}</h3>
                <p className="text-sm text-deep-blue/60 mt-2">{v.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-5 lg:px-10">
          <h2 className="font-display text-deep-blue text-4xl sm:text-5xl mb-8">{t("about.teamTitle")}</h2>
          {team.length === 0 ? (
            <p className="text-deep-blue/60 italic">{t("about.teamEmpty")}</p>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
              {team.map(m => (
                <div key={m.id} className="card-elev rounded-2xl p-5 text-center" data-testid={`team-${m.id}`}>
                  {m.photo ? <img src={m.photo} alt={m.name} className="w-20 h-20 rounded-full mx-auto object-cover border-2 border-gold/40 shadow-sm"/> : <div className="w-20 h-20 rounded-full mx-auto bg-gradient-to-br from-warm-orange to-gold grid place-items-center text-deep-blue font-display text-2xl font-bold">{m.name?.charAt(0)}</div>}
                  <div className="font-display text-xl text-deep-blue mt-4">{m.name}</div>
                  <div className="text-xs uppercase tracking-widest text-warm-orange mt-1">{m.role}</div>
                  {m.bio && <p className="text-sm text-deep-blue/60 mt-3">{m.bio}</p>}
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* FONDATEUR */}
      <section className="py-24 bg-pearl" data-testid="founder-section">
        <div className="max-w-6xl mx-auto px-5 lg:px-10 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-4">
            {site.founder_photo ? (
              <img src={site.founder_photo} alt={site.founder_name || "Fondateur"} className="w-full aspect-square rounded-2xl object-cover border-4 border-gold/30 shadow-xl"/>
            ) : (
              <div className="w-full aspect-square rounded-2xl bg-gradient-to-br from-deep-blue to-warm-orange grid place-items-center">
                <span className="font-display text-ivory text-8xl italic">{(site.founder_name || "F").charAt(0)}</span>
              </div>
            )}
          </div>
          <div className="lg:col-span-8">
            <div className="text-xs uppercase tracking-[0.3em] text-warm-orange font-semibold">{t("about.founderTag")}</div>
            <h2 className="font-display text-5xl sm:text-6xl text-deep-blue mt-3">{site.founder_name || <span className="italic text-deep-blue/40">Nom à compléter</span>}</h2>
            <div className="text-warm-orange font-semibold mt-2 uppercase tracking-widest text-sm">{site.founder_role || "Fondateur·rice · Larmes en Lumière"}</div>
            {site.founder_quote && (
              <div className="mt-6 p-6 rounded-xl bg-white border-l-4 border-gold">
                <Quote className="text-gold mb-2" size={24}/>
                <p className="font-display italic text-2xl text-deep-blue leading-relaxed">« {site.founder_quote} »</p>
              </div>
            )}
            {site.founder_bio ? (
              <p className="text-deep-blue/80 mt-6 leading-relaxed whitespace-pre-line">{site.founder_bio}</p>
            ) : (
              <p className="text-deep-blue/50 italic mt-6">{t("about.founderMissing")}</p>
            )}
          </div>
        </div>
      </section>

      {/* TÉMOIGNAGES DES FAMILLES */}
      <section className="py-24 bg-ivory" data-testid="testimonials-section">
        <div className="max-w-6xl mx-auto px-5 lg:px-10">
          <div className="text-xs uppercase tracking-[0.3em] text-warm-orange font-semibold">{t("about.testimonialsTag")}</div>
          <h2 className="font-display text-deep-blue text-5xl sm:text-6xl mt-3">{t("about.testimonialsT1")}<br/><span className="italic text-warm-orange">{t("about.testimonialsT2")}</span></h2>
          {testimonials.length === 0 ? (
            <div className="mt-10 p-8 rounded-2xl bg-pearl text-deep-blue/60 italic">{t("about.testimonialsEmpty")}</div>
          ) : (
            <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {testimonials.map(tm => (
                <article key={tm.id} className="card-elev rounded-2xl p-8 relative overflow-hidden" data-testid={`testimonial-${tm.id}`}>
                  <Quote className="text-gold absolute top-4 right-4 opacity-30" size={48}/>
                  <p className="font-display italic text-lg text-deep-blue leading-relaxed">« {tm.quote} »</p>
                  <div className="mt-6 pt-4 border-t border-black/5 flex items-center gap-3">
                    {tm.photo ? (
                      <img src={tm.photo} alt={tm.name} className="w-12 h-12 rounded-full object-cover border-2 border-gold"/>
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-warm-orange to-gold grid place-items-center text-deep-blue font-bold">{(tm.name || "?").charAt(0)}</div>
                    )}
                    <div>
                      <div className="font-semibold text-deep-blue">{tm.name || "Anonyme"}</div>
                      <div className="text-xs text-deep-blue/60">{tm.role || "Famille accompagnée"}{tm.location ? ` · ${tm.location}` : ""}</div>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
